import { useIsAuthenticated } from '@/auth/authentication';
import { useEffectiveFlagOverrides, useRecordQueriedFlags } from '@/featureFlags/flagOverrideState';
import { applyFlagOverrides } from '@/featureFlags/flagOverrides';
import { toEnabledByFlagName } from '@/featureFlags/flagTelemetry';
import { useTrackFeatureFlagValues } from '@/utils/datadogSessionAttributes';
import { TypedDocumentNode, gql } from '@apollo/client';
import { skipToken, useQuery, useSuspenseQuery } from '@apollo/client/react';
import { useMemo } from 'react';

type FeatureFlagsData = { featureFlags: { id: string; enabled: boolean }[] };
type FeatureFlagsVariables = { features: string[] };

const FEATURE_FLAGS_QUERY: TypedDocumentNode<FeatureFlagsData, FeatureFlagsVariables> = gql`
    query FeatureFlags($features: [String!]!) {
        featureFlags(keys: $features) {
            id
            enabled
        }
    }
`;

// Transform the response into key-value pairs as follows:
// {
//     [feature1]: true,
//     [feature2]: false,
//     ...
//     [featureN]: [featureN is enabled]
// }
//
// If passed a key that is not actually a feature flag, it will return false.
const toEnabledByKey = (
    features: Record<string, string>,
    data: FeatureFlagsData | undefined,
): Record<string, boolean> =>
    Object.keys(features).reduce<Record<string, boolean>>((acc, key) => {
        const match = data?.featureFlags.find((flag) => flag.id === features[key]);
        acc[key] = match?.enabled ?? false;
        return acc;
    }, {});

// Returns a record of feature flag names to whether they are enabled. Feature flag values will be refetched when the auth state changes.
// If passed a key that is not actually a feature flag, it will return false.
// Values a session is forcing through the flag override tools take precedence over the server's.
export const useFeatureFlagsEnabled = (
    features: Record<string, string>,
    options?: { skip?: boolean },
) => {
    const featuresArray = useMemo(() => Object.values(features), [features]);
    const isAuthed = useIsAuthenticated();
    const overrides = useEffectiveFlagOverrides();
    const isQuerying = !(options?.skip ?? false) && isAuthed;
    useRecordQueriedFlags(featuresArray, isQuerying);

    const { data } = useSuspenseQuery(
        FEATURE_FLAGS_QUERY,
        options?.skip || !isAuthed ? skipToken : { variables: { features: featuresArray } },
    );

    const enabled = applyFlagOverrides(features, toEnabledByKey(features, data), overrides);
    useTrackFeatureFlagValues(toEnabledByFlagName(features, enabled), data !== undefined);

    return enabled;
};

// Convenience function for when you only have one feature flag.
export const useFeatureFlagEnabled = (feature: string, options?: { skip?: boolean }) => {
    return useFeatureFlagsEnabled({ [feature]: feature }, options)[feature];
};

// Non-suspending variant, for callers that already render a loading state and would
// otherwise get a Suspense fallback on top of it. Shares the cache with the hooks above.
export const useFeatureFlagsEnabledQuery = (
    features: Record<string, string>,
    options?: { skip?: boolean },
) => {
    const featuresArray = useMemo(() => Object.values(features), [features]);
    const isAuthed = useIsAuthenticated();
    const overrides = useEffectiveFlagOverrides();
    const isQuerying = isAuthed && options?.skip !== true;
    useRecordQueriedFlags(featuresArray, isQuerying);

    const { data, loading } = useQuery(FEATURE_FLAGS_QUERY, {
        variables: { features: featuresArray },
        skip: !isAuthed || options?.skip === true,
    });

    const enabled = applyFlagOverrides(features, toEnabledByKey(features, data), overrides);
    useTrackFeatureFlagValues(
        toEnabledByFlagName(features, enabled),
        !loading && data !== undefined,
    );

    return { enabled, loading };
};

// Convenience function for when you only have one feature flag.
export const useFeatureFlagEnabledQuery = (feature: string, options?: { skip?: boolean }) => {
    const { enabled, loading } = useFeatureFlagsEnabledQuery({ [feature]: feature }, options);
    return { enabled: enabled[feature], loading };
};
